// The shape of a destination's registry (docs/specs/MODULE_NAVIGATION.md). Each place in the left menu (Home,
// Prospects, Conversations, Quotes, Contacts, Results, Settings) and each paused module (Attract, Network, ReConnect)
// has one file in this folder that exports a ModuleDef. The app's routes, the left menu, the tabs, the side list of
// pages, the breadcrumbs, the waiting counts and TASK_HOMES are all worked out from these files (lib/nav.ts and
// modules/routes.tsx), so changing a destination's pages means editing only its own registry.
import type { ReactNode } from "react";

/** The five steps the paused modules (Attract, Network, ReConnect) still use as their tabs, in this order. */
export const STEP_KEYS = ["accounts", "rules", "research", "actions", "performance"] as const;
export type StepKey = (typeof STEP_KEYS)[number];

export const STEP_LABELS: Record<StepKey, string> = {
  accounts: "Accounts",
  rules: "Rules",
  research: "Research",
  actions: "Actions",
  performance: "Performance",
};

/** A paused module's five steps as tabs, in STEP_KEYS order. */
export function fiveSteps(steps: Record<StepKey, ModulePage[]>): ModuleTab[] {
  return STEP_KEYS.map((key) => ({ key, label: STEP_LABELS[key], pages: steps[key] }));
}

/**
 * A page inside a tab: `/<module>/<tab>/<slug>`, or `/<module>/<tab>` for a slug of "" (a tab's own page, always its
 * first). Either it renders something, or it says what is coming.
 */
export type ModulePage = {
  /** Lower-case, hyphenated, unique within the tab: the last part of the address. "" is the tab's own address. */
  slug: string;
  /** The name in the side list and the breadcrumb. */
  label: string;
  /** The process step id (process/steps.yaml) the page belongs to, when there is one. */
  step?: string;
  /**
   * Addresses under the page, relative to it, for detail views: `{ path: ":id", render }` opens at
   * `/<module>/<step>/<slug>/:id` and counts as the page in the menu and breadcrumb.
   */
  routes?: { path: string; render: () => ReactNode }[];
  /**
   * Shown in the side list only when the tab's Advanced switch is on (Settings' detail, 29 Oct 2026). The address always
   * opens, and opening it shows the advanced pages.
   */
  advanced?: boolean;
  /**
   * The set-up part this page serves, as "<module>.<part>" (lib/setupCatalogue.ts, for example "find.calls"). When
   * set-up has recorded that part as off, the page stays in its place in the side list marked "Off" (its address still
   * opens), so the list never changes shape.
   */
  part?: string;
} & (
  | {
      /** What the page shows. A function, so the registry does not touch the screens until the page opens. */
      render: () => ReactNode;
      comingSoon?: never;
    }
  | {
      render?: never;
      /** One or two plain sentences on what the page will do. The page shows a "coming soon" note with it. */
      comingSoon: string;
    }
);

/** A page with its own full address: a flat module's menu items and extra pages. */
export interface ModuleRoute {
  path: string;
  label: string;
  step?: string;
  render: () => ReactNode;
}

interface ModuleBase {
  /** The first part of the module's addresses, and its key in the menu ("pipeline" for Connect). */
  key: string;
  /** The name in the left menu. */
  label: string;
  /**
   * Where each kind of task waiting for a person can be acted on in this module: kind → page addresses. Latest
   * Updates shows every task as well. Other modules and the business-wide sections may list the same kind; the
   * homes are joined in menu order.
   */
  taskHomes: Record<string, string[]>;
  /** Pages reachable by address but not in the menu; they sit under this module in the breadcrumb. */
  extraPages?: ModuleRoute[];
  /**
   * Switched off for now (Craig, 22 Oct 2026: Find, Connect and the CRM first). What the module will do, in a line.
   * The menu shows it greyed as "Soon" with no pages, and every address under it opens a Coming soon page. Delete the
   * line to bring the module back.
   */
  paused?: string;
  /**
   * The set-up part ("<module>.<part>") that puts this destination in the menu: hidden while set-up records it as off,
   * shown when on or when set-up can't tell (a business from before guided set-up). Its addresses always open.
   */
  shownWithPart?: string;
}

/** One tab across the top of a destination, with its pages down the side when it has more than one. */
export interface ModuleTab {
  /** The tab's part of the address: `/<module>/<key>`. */
  key: string;
  label: string;
  /** At least one. The first is the tab's landing page; a slug of "" makes it the tab's own address. */
  pages: ModulePage[];
}

/** A destination with tabs across the top, each tab's pages down the side. */
export interface SteppedModule extends ModuleBase {
  layout: "steps";
  /** The tabs, in order. The first tab is where the destination opens, unless `landing` decides. */
  tabs: ModuleTab[];
  /**
   * Shown across the top of every page of the module, above the step tabs. Keep it light: it renders on every page of
   * the module. (Find's guided path used it until 20 Oct 2026; every module now shows its agents indicator instead.)
   */
  header?: () => ReactNode;
  /**
   * What `/<module>` opens, when it depends on the business. The element redirects; without it `/<module>` opens the
   * first tab's first page. The menu entry links to `/<module>` when this is set.
   */
  landing?: () => ReactNode;
}

/** A destination with one row of page tabs (Home, Quotes, Contacts). */
export interface FlatModule extends ModuleBase {
  layout: "flat";
  items: ModuleRoute[];
  /** Where the module's entry in the left menu leads; the first item when left out. */
  home?: string;
}

export type ModuleDef = SteppedModule | FlatModule;

/** `/attract/actions/posts`, or `/campaigns/to-approve` for a tab's own page (slug ""). */
export function pagePath(module: string, tab: string, slug: string): string {
  return slug ? `/${module}/${tab}/${slug}` : `/${module}/${tab}`;
}

export function stepPath(module: string, tab: string): string {
  return `/${module}/${tab}`;
}
